import React, { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CheckCircle2, Zap, ShieldCheck, Wallet, Star, ArrowRight, ChevronDown,
  Home as HomeIcon, Store, Briefcase, Search, MapPin, Sparkles, MessageCircle,
} from "lucide-react";
import { useCatalog } from "../contexts/CatalogContext";
import TestimonialsSection from "../components/home/TestimonialsSection";
import CoverageSection from "../components/home/CoverageSection";
import FaqSection from "../components/home/FaqSection";

const ALL_BRANDS_DOUBLED = [];

const BADGE_ICONS = { "check-circle": CheckCircle2, zap: Zap, "shield-check": ShieldCheck, wallet: Wallet, star: Star };

const SegIcon = ({ id, className }) => {
  if (id === "home") return <HomeIcon className={className} />;
  if (id === "shop") return <Store className={className} />;
  return <Briefcase className={className} />;
};

export default function Home() {
  const navigate = useNavigate();
  const [city, setCity] = useState("lucknow");
  const { categories, CITIES, SEGMENTS, TRUST_BADGES } = useCatalog();
  const orderedCategories = useMemo(() => {
    const roCategory = categories.find((c) => c.id === "ro");
    if (!roCategory) return categories;
    return [roCategory, ...categories.filter((c) => c.id !== "ro")];
  }, [categories]);

  const ALL_BRANDS = Array.from(new Set(categories.flatMap((c) => c.brands)));
  const ALL_BRANDS_DOUBLED = [...ALL_BRANDS, ...ALL_BRANDS].map((name, idx) => ({ id: `${name}-${idx}`, name }));
  const FEATURED_SERVICES = categories.flatMap((c) => c.services.slice(0, 3).map((s) => ({ ...s, cat: c }))).slice(0, 12);
  const liveCategories = categories.filter((c) => c.status !== "soon");
  const upcomingCategories = categories.filter((c) => c.status === "soon");

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden bg-slate-950 text-white" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-[#081E29] via-[#0F5872] to-[#0D9EA5] opacity-95" />
        <div className="absolute -left-16 top-10 w-72 h-72 rounded-full bg-[#22C55E]/20 blur-3xl" />
        <div className="absolute right-0 top-24 w-96 h-96 rounded-full bg-[#0EA5A4]/20 blur-3xl" />
        <div className="absolute left-1/2 bottom-0 w-80 h-80 -translate-x-1/2 rounded-full bg-[#0891B2]/10 blur-3xl" />
        <div className="absolute inset-0 grain mix-blend-screen opacity-40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 text-white">
              <a
                href="https://bot.repairro.nexitytec.cloud"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex w-fit items-center gap-2 px-4 py-2.5 rounded-full bg-gradient-to-r from-[#22C55E]/35 via-[#16A34A]/30 to-[#0891B2]/30 backdrop-blur border border-[#86EFAC]/70 text-sm font-semibold text-white shadow-[0_0_0_1px_rgba(255,255,255,0.08),0_12px_35px_rgba(34,197,94,0.25)] hover:shadow-[0_0_0_1px_rgba(255,255,255,0.12),0_16px_40px_rgba(34,197,94,0.35)] transition-all hover:scale-[1.03] animate-[pulse_1.8s_ease-in-out_infinite]"
              >
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-[#A7F3D0] opacity-75 animate-ping"></span>
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#ECFCCB]"></span>
                </span>
                <MessageCircle className="w-4 h-4 text-[#F0FDF4]" />
                Ask AI about RO
              </a>
              <p className="mt-3 max-w-xl text-sm text-slate-200/90">
                Chat with our AI assistant for booking enquiries, service details, and quick RO support.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur text-xs font-semibold text-white border border-white/20 shadow-lg shadow-slate-950/20">
                <Sparkles className="w-3.5 h-3.5 text-[#A5F3FC]" /> Trusted by 500+ homes & offices
              </span>
              <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl mt-5 leading-tight tracking-tight">
                Repairro Service Care, Delivered Fast
              </h1>
              <p className="mt-5 text-lg text-slate-200 max-w-2xl">
                Repairro brings expert RO installation, repair, AMC and cleaning service to your doorstep with transparent pricing and same-day support in Lucknow.
              </p>

              {/* City selector + Book Now */}
              <div className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl">
                <div className="relative flex-1">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <select
                    data-testid="hero-city-select"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="w-full pl-12 pr-10 py-4 rounded-xl bg-white text-slate-900 font-medium appearance-none focus:outline-none focus:ring-2 focus:ring-[#0891B2]"
                  >
                    {CITIES.map((c) => (
                      <option key={c.id} value={c.id} disabled={c.status === "soon"}>
                        {c.name} {c.status === "soon" ? "(Coming soon)" : ""}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    data-testid="hero-book-now-btn"
                    onClick={() => navigate("/booking")}
                    className="bg-[#0891B2] hover:bg-[#0C7A89] text-white font-semibold px-7 py-4 rounded-xl transition-all shadow-lg flex items-center justify-center gap-2"
                  >
                    Book Now <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Inline trust row */}
              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-200">
                <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[#22C55E]" /> Verified Technicians</span>
                <span className="flex items-center gap-1.5"><Wallet className="w-4 h-4 text-[#22C55E]" /> No Hidden Charges</span>
                <span className="flex items-center gap-1.5"><Star className="w-4 h-4 text-[#0EA5A4] fill-[#0EA5A4]" /> 4.8/5 Rating</span>
              </div>
            </div>

            {/* Quick-pick service card */}
            <div className="lg:col-span-5">
              <div className="bg-white rounded-2xl p-6 sm:p-7 card-shadow">
                <div className="flex items-center gap-2 text-xs font-semibold text-[#1A3C6E] uppercase tracking-wider">
                  <Search className="w-4 h-4" /> Quick Book
                </div>
                <h3 className="font-display font-bold text-xl text-slate-900 mt-2">What service do you need?</h3>
                <div className="grid grid-cols-2 gap-3 mt-5">
                  {orderedCategories.map((c) => {
                    const Icon = c.icon;
                    if (c.status === "soon") {
                      return (
                        <div key={c.id} data-testid={`hero-quick-${c.id}`} className="border border-dashed border-slate-200 rounded-xl p-4 opacity-70">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: c.bg, color: c.color }}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="font-semibold text-slate-900 text-sm">{c.short}</div>
                          <div className="text-xs text-slate-500 mt-0.5">Coming soon</div>
                        </div>
                      );
                    }
                    return (
                      <Link
                        key={c.id}
                        to={`/booking?category=${c.id}`}
                        data-testid={`hero-quick-${c.id}`}
                        className="group border border-slate-200 hover:border-[#1A3C6E] hover:shadow-md rounded-xl p-4 transition-all"
                      >
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                          style={{ backgroundColor: c.bg, color: c.color }}
                        >
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="font-semibold text-slate-900 text-sm">{c.short}</div>
                        <div className="text-xs text-slate-500 mt-0.5">From ₹{c.startingPrice}</div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORY CARDS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24" data-testid="category-section">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <span className="text-xs font-semibold text-[#0891B2] uppercase tracking-widest">Our Services</span>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 mt-2">RO services. More categories coming soon.</h2>
          </div>
          <Link to="/services" data-testid="see-all-services-link" className="text-sm font-semibold text-[#0F766E] hover:text-[#0891B2] flex items-center gap-1">
            See all services <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {categories.map((c) => {
            const Icon = c.icon;
            const disabled = c.status === "soon";
            return (
              <div
                key={c.id}
                data-testid={`category-card-${c.id}`}
                className={`bg-white rounded-2xl p-6 border border-slate-200 transition-all card-shadow ${disabled ? "opacity-70" : "hover:border-[#0F766E] hover:-translate-y-1"}`}
              >
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-5"
                  style={{ backgroundColor: c.bg, color: c.color }}
                >
                  <Icon className="w-7 h-7" />
                </div>
                <h3 className="font-display font-bold text-lg text-slate-900">{c.name}</h3>
                <p className="text-sm text-slate-500 mt-1">{c.tagline}</p>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="text-xs text-slate-500">{disabled ? "Launching soon" : "Starting from"}</span>
                  <span className="font-display font-bold text-xl text-[#0F766E]">{disabled ? "—" : `₹${c.startingPrice}`}</span>
                </div>
                {disabled ? (
                  <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-slate-500">Coming soon</div>
                ) : (
                  <Link
                    to={`/booking?category=${c.id}`}
                    data-testid={`category-book-${c.id}`}
                    className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#0891B2] hover:text-[#0C7A89]"
                  >
                    Book {c.short} <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* SEGMENTS */}
      <section className="bg-white border-y border-slate-200" data-testid="segments-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-semibold text-[#0891B2] uppercase tracking-widest">Built for everyone</span>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 mt-2">Serving every kind of customer</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {SEGMENTS.map((s) => (
              <div key={s.id} data-testid={`segment-card-${s.id}`} className="bg-[#F8F9FB] rounded-2xl p-7 border border-slate-200">
                <div className="w-12 h-12 rounded-xl bg-[#0F766E] text-white flex items-center justify-center mb-5">
                  <SegIcon id={s.id} className="w-6 h-6" />
                </div>
                <h3 className="font-display font-bold text-lg text-slate-900">{s.name}</h3>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed">{s.desc}</p>
                <Link
                  to={`/booking?segment=${s.id}`}
                  data-testid={`segment-book-${s.id}`}
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#0F766E] hover:text-[#0891B2]"
                >
                  {s.cta} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24" data-testid="how-it-works">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs font-semibold text-[#0891B2] uppercase tracking-widest">How it works</span>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 mt-2">Book in seconds. Done in hours.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { n: "01", t: "Choose & Book Online", d: "Pick the service, brand and a slot. Takes under 60 seconds." },
            { n: "02", t: "Verified Technician Visits", d: "Aadhar-verified, uniformed technician arrives at your chosen slot." },
            { n: "03", t: "Job Done, Pay Later", d: "Pay only after satisfactory completion — UPI, cards or cash." },
          ].map((s) => (
            <div key={s.n} data-testid={`step-${s.n}`} className="relative bg-white rounded-2xl p-7 border border-slate-200 card-shadow">
              <div className="font-display font-extrabold text-5xl text-[#0891B2]/20">{s.n}</div>
              <h3 className="font-display font-bold text-lg text-slate-900 mt-2">{s.t}</h3>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICE PRICING CARDS */}
      <section className="bg-white border-y border-slate-200" data-testid="pricing-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
            <div>
              <span className="text-xs font-semibold text-[#0891B2] uppercase tracking-widest">Transparent Pricing</span>
              <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 mt-2">Most-booked services</h2>
            </div>
            <Link to="/pricing" data-testid="see-all-pricing" className="text-sm font-semibold text-[#0F766E] hover:text-[#0891B2] flex items-center gap-1">
              View full pricing <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {FEATURED_SERVICES.map((s) => {
              const Icon = s.cat.icon;
              return (
                <Link
                  key={s.id}
                  to={`/booking?category=${s.cat.id}&service=${s.id}`}
                  data-testid={`price-card-${s.id}`}
                  className="bg-white rounded-xl p-4 border border-slate-200 hover:border-[#0891B2] hover:shadow-md transition-all"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ backgroundColor: s.cat.bg, color: s.cat.color }}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className="text-xs text-slate-500">{s.cat.short}</span>
                  </div>
                  <h4 className="font-semibold text-sm text-slate-900 leading-snug">{s.name}</h4>
                  <div className="mt-3 font-display font-bold text-lg text-[#0F766E]">
                    ₹{s.price}
                    {s.id.includes("amc") && <span className="text-xs font-medium text-slate-500">/yr</span>}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* BRANDS MARQUEE */}
      <section className="py-16" data-testid="brands-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="text-xs font-semibold text-[#0891B2] uppercase tracking-widest">Brands we service</span>
            <h2 className="font-display font-bold text-2xl sm:text-3xl text-slate-900 mt-2">Every major brand. Original spares.</h2>
          </div>
        </div>
        <div className="overflow-hidden bg-white border-y border-slate-200 py-8">
          <div className="marquee-track flex gap-12 whitespace-nowrap">
            {ALL_BRANDS_DOUBLED.map((b) => (
              <span key={b.id} className="font-display font-bold text-2xl text-slate-400 hover:text-[#0F766E] transition-colors">
                {b.name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST BADGES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8" data-testid="trust-badges">
        <div className="bg-[#0F172A] rounded-2xl px-6 py-8 grid grid-cols-2 md:grid-cols-5 gap-6">
          {TRUST_BADGES.map((b) => {
            const Icon = BADGE_ICONS[b.icon] || CheckCircle2;
            return (
              <div key={b.label} className="flex items-center gap-3 text-white">
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#0EA5A4]" />
                </div>
                <span className="text-sm font-semibold leading-tight">{b.label}</span>
              </div>
            );
          })}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <TestimonialsSection />

      {/* COVERAGE */}
      <CoverageSection />

      {/* FAQ */}
      <FaqSection />

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-testid="final-cta">
        <div className="bg-gradient-to-r from-[#0F766E] to-[#0891B2] rounded-3xl p-10 sm:p-14 text-center relative overflow-hidden">
          <div className="absolute inset-0 grain opacity-30" />
          <div className="relative">
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-white">Ready for hassle-free home service?</h2>
            <p className="mt-3 text-slate-300 max-w-xl mx-auto">Book your slot in 60 seconds. We'll handle the rest.</p>
            <Link
              to="/booking"
              data-testid="final-cta-book"
              className="inline-flex items-center gap-2 mt-7 bg-white text-[#0F766E] hover:bg-slate-50 font-semibold px-7 py-4 rounded-xl transition-all shadow-lg"
            >
              Book a Service <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
